// recaptcha-v3.service.ts
import { Injectable, NgZone } from '@angular/core';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class RecaptchaV3Service {
  private readonly siteKey = '6LcOElcrAAAAAKrUAmmv-CJ4HOCDOYxtsDFMc9_s';
  private scriptLoaded = false;
  private scriptLoading: Promise<void> | null = null;
  
  constructor(private ngZone: NgZone) {}

  /**
   * Dynamically load reCAPTCHA script
   */
  private loadScript(): Promise<void> {
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    if (this.scriptLoading) {
      return this.scriptLoading;
    }

    this.scriptLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };

      script.onerror = () => {
        this.scriptLoading = null;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      document.head.appendChild(script);
    });

    return this.scriptLoading;
  }

  /**
   * Execute reCAPTCHA v3 and get token
   * @param action - The action name for this request (e.g., 'upload', 'submit', 'login')
   * @returns Promise with the token
   */
  async execute(action: string = 'submit'): Promise<string> {
    // Load script if not already loaded
    await this.loadScript();
    
    // Wait for grecaptcha to be ready
    await this.waitForGrecaptcha();

    return new Promise((resolve, reject) => {
      this.ngZone.runOutsideAngular(() => {
        try {
          window.grecaptcha.ready(() => {
            window.grecaptcha.execute(this.siteKey, { action })
              .then((token: string) => {
                this.ngZone.run(() => {
                  resolve(token);
                });
              })
              .catch((error: any) => {
                this.ngZone.run(() => {
                  console.error('reCAPTCHA v3 execution error:', error);
                  reject(error);
                });
              });
          });
        } catch (error) {
          this.ngZone.run(() => {
            console.error('reCAPTCHA v3 error:', error);
            reject(error);
          });
        }
      });
    });
  }

  /**
   * Wait for grecaptcha to be available
   */
  private waitForGrecaptcha(): Promise<void> {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        resolve();
        return;
      }

      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      
      const checkInterval = setInterval(() => {
        attempts++;
        
        if (window.grecaptcha && window.grecaptcha.ready) {
          clearInterval(checkInterval);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          console.error('reCAPTCHA failed to load after 5 seconds');
          resolve();
        }
      }, 100);
    });
  }

  /**
   * Check if reCAPTCHA is loaded
   */
  isLoaded(): boolean {
    return this.scriptLoaded && window.grecaptcha && window.grecaptcha.ready;
  }

  /**
   * Get the site key
   */
  getSiteKey(): string {
    return this.siteKey;
  }

  /**
   * Clean up reCAPTCHA badge if needed
   */
  cleanup(): void {
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) {
      badge.remove();
    }
  }
}